import React, { useEffect } from 'react';
import { Compass, Smartphone } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';

// Google My Maps embed - villa/resort locations, hiking trails, dining spots and
// excursion companies pinned across St John. The "/d/embed" path is Google's
// iframe-safe URL (the "/d/viewer...femb=1" variant sends X-Frame-Options: SAMEORIGIN
// and Google refuses to render it inside our page).
const MAP_EMBED_URL = 'https://www.google.com/maps/d/embed?mid=1JW0b4oIZmCCEmTICc4WB0xXWqqvNMe0&ehbc=2E312F';
// Same map without femb=1 - opens the full interactive Google Maps view, which is the
// friendliest option on a phone (it offers to open straight in the Google Maps app).
const MAP_MOBILE_URL = 'https://www.google.com/maps/d/viewer?mid=1JW0b4oIZmCCEmTICc4WB0xXWqqvNMe0';
const GOOGLE_EARTH_URL =
  'https://earth.google.com/web/@18.3354995,-64.7666125,214.15169709a,11262.44511805d,30y,0h,0t,0r/data=CgRCAggBMigKJgokCiAxSlcwYjRvSVptQ0NFbVRJQ2M0V0IweFhXcXF2Tk1lMCACOgMKATBCAggASggImLGAswcQAQ?utm_source=mymaps';

const IslandActivitiesMapPage = () => {
  useEffect(() => {
    setSeoMetadata({
      title: 'Island Activities Map | St John Villas, Trails & Excursions | St John USVI',
      description:
        'Our interactive Island Activities Map pins villa and resort locations, hiking trailheads, dining spots and excursion companies across St John, USVI - open it full screen, on your phone, or in Google Earth.',
      keywords: 'st john map, island activities map, st john villa locations map, st john hiking trail map, st john things to do map',
      canonicalPath: '/island-activities-map',
      ogType: 'website',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 pb-20 lg:pt-32">
        <div className="mx-auto max-w-6xl px-4">
          <header className="mb-6 text-center">
            <h1 className="text-3xl font-extrabold uppercase italic tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Island Activities Map
            </h1>
            <p className="font-script mt-2 text-2xl text-cyan-500 sm:text-3xl">St John, Virgin Islands</p>
            <p className="mx-auto mt-4 max-w-2xl text-slate-700 leading-relaxed">
              Villa &amp; resort locations, hiking trailheads, dining spots and excursion companies, all pinned in one
              place. Explore it below, open it full screen on your phone, or fly around it in Google Earth.
            </p>
          </header>

          <div className="mb-6 flex flex-wrap items-center justify-center gap-3" data-testid="map-action-buttons">
            <a
              href={GOOGLE_EARTH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-red-700"
              data-testid="map-open-google-earth"
            >
              <Compass className="h-4 w-4" /> Open on Google Earth
            </a>
            <a
              href={MAP_MOBILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-300 px-6 py-3 text-sm font-bold uppercase tracking-wide text-slate-700 transition hover:border-red-300 hover:text-red-600"
              data-testid="map-open-mobile"
            >
              <Smartphone className="h-4 w-4" /> View on Mobile
            </a>
          </div>

          <div
            className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm"
            style={{ height: '75vh', minHeight: 480 }}
            data-testid="map-embed-wrapper"
          >
            <iframe
              title="Island Activities Map"
              src={MAP_EMBED_URL}
              className="h-full w-full"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-embed-iframe"
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IslandActivitiesMapPage;
